import { create } from 'zustand'

import type { IdeaPreview } from '../model/IdeaPreview'


type IdeaLikeState = {
  id: string
  isLikedByCurrentUser: boolean
  likesCount: number
}

type LikeMutationResult = {
  ideaId: string
  liked: boolean
  likesCount: number
}

interface IdeasBankStore {
  ideas: Record<string, IdeaPreview>
  likeStateById: Record<string, IdeaLikeState>
  selectedIdeaId?: string
  pendingLikeMutations: Record<string, boolean>
  upsertIdeas: (ideas: IdeaPreview[]) => void
  setSelectedIdeaId: (ideaId?: string) => void
  getIdeaById: (ideaId: string) => IdeaPreview | undefined
  getLikeStateById: (ideaId: string) => IdeaLikeState | undefined
  setLikeStateFromIdea: (idea: IdeaPreview) => void
  setLikeState: (result: LikeMutationResult) => void
  toggleIdeaLike: (ideaId: string) => Promise<LikeMutationResult | undefined>
}

const getNormalizedLikeState = (idea: IdeaPreview): IdeaLikeState => ({
  id: idea.id,
  isLikedByCurrentUser: Boolean(idea.isLikedByCurrentUser),
  likesCount: idea.likesCount ?? 0,
})

const updateIdeaSnapshot = (
  idea: IdeaPreview,
  result: LikeMutationResult,
): IdeaPreview => ({
  ...idea,
  isLikedByCurrentUser: result.liked,
  likesCount: result.likesCount,
})

export const useIdeasBankStore = create<IdeasBankStore>((set, get) => ({
  ideas: {},
  likeStateById: {},
  pendingLikeMutations: {},
  upsertIdeas: (ideas) => {
    set((state) => {
      const nextIdeas = { ...state.ideas }
      const nextLikeState = { ...state.likeStateById }

      ideas.forEach((idea) => {
        nextIdeas[idea.id] = {
          ...idea,
          isLikedByCurrentUser: idea.isLikedByCurrentUser ?? false,
          likesCount: idea.likesCount ?? 0,
        }
        nextLikeState[idea.id] = getNormalizedLikeState(nextIdeas[idea.id])
      })

      return {
        ideas: nextIdeas,
        likeStateById: nextLikeState,
        selectedIdeaId: state.selectedIdeaId ?? ideas[0]?.id,
      }
    })
  },
  setSelectedIdeaId: (ideaId) => set({ selectedIdeaId: ideaId }),
  getIdeaById: (ideaId) => get().ideas[ideaId],
  getLikeStateById: (ideaId) => get().likeStateById[ideaId],
  setLikeStateFromIdea: (idea) => {
    set((state) => ({
      ideas: {
        ...state.ideas,
        [idea.id]: {
          ...idea,
          isLikedByCurrentUser: idea.isLikedByCurrentUser ?? false,
          likesCount: idea.likesCount ?? 0,
        },
      },
      likeStateById: {
        ...state.likeStateById,
        [idea.id]: getNormalizedLikeState(idea),
      },
    }))
  },
  setLikeState: (result) => {
    set((state) => {
      const currentIdea = state.ideas[result.ideaId]

      return {
        ideas: currentIdea
          ? {
              ...state.ideas,
              [result.ideaId]: updateIdeaSnapshot(currentIdea, result),
            }
          : state.ideas,
        likeStateById: {
          ...state.likeStateById,
          [result.ideaId]: {
            id: result.ideaId,
            isLikedByCurrentUser: result.liked,
            likesCount: result.likesCount,
          },
        },
      }
    })
  },
  toggleIdeaLike: async (ideaId) => {
    // Intentionally left as a no-op wrapper for backward compatibility.
    // Like/unlike is now performed by TanStack Query (see useToggleIdeaLikeMutation).
    const state = get()
    const idea = state.ideas[ideaId]

    if (!idea) return undefined

    const nextLikedState = !Boolean(idea.isLikedByCurrentUser)
    const nextLikesCount = Math.max(0, (idea.likesCount ?? 0) + (nextLikedState ? 1 : -1))

    set((current) => ({
      pendingLikeMutations: {
        ...current.pendingLikeMutations,
        [ideaId]: true,
      },
      ideas: {
        ...current.ideas,
        [ideaId]: {
          ...idea,
          isLikedByCurrentUser: nextLikedState,
          likesCount: nextLikesCount,
        },
      },
      likeStateById: {
        ...current.likeStateById,
        [ideaId]: {
          id: ideaId,
          isLikedByCurrentUser: nextLikedState,
          likesCount: nextLikesCount,
        },
      },
    }))

    // Caller should reconcile using the mutation success result.
    set((current) => ({
      pendingLikeMutations: {
        ...current.pendingLikeMutations,
        [ideaId]: false,
      },
    }))

    return {
      ideaId,
      liked: nextLikedState,
      likesCount: nextLikesCount,
    }
  },
}))

